import { useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { ServicesData } from "../../config/ServiceData";
import AnimatedHeading from "../../animations/AnimatedHeading";
import ContactModal from "../../../Assets/Common/ContactModal";

// ─────────────────────────────────────────────────────────────
// ANIMATION
// ─────────────────────────────────────────────────────────────

const ease = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 44,
  },

  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease,
    },
  },
};

const scaleUp = {
  hidden: {
    opacity: 0,
    scale: 0.88,
  },

  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease,
    },
  },
};

const slideL = {
  hidden: {
    opacity: 0,
    x: -50,
  },

  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.55,
      ease,
    },
  },
};

const slideR = {
  hidden: {
    opacity: 0,
    x: 50,
  },

  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.55,
      ease,
    },
  },
};

const stagger = {
  hidden: {},

  visible: {
    transition: {
      staggerChildren: 0.11,
    },
  },
};

// ─────────────────────────────────────────────────────────────
// REVEAL
// ─────────────────────────────────────────────────────────────

function Reveal({
  children,
  variants = fadeUp,
  className = "",
}) {
  const ref = useRef(null);

  const inView = useInView(ref, {
    once: true,
    amount: 0.2,
  });

  return (
    <motion.div
      ref={ref}
      variants={variants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────
// 404
// ─────────────────────────────────────────────────────────────

function ServiceNotFound() {
  return (
    <div
      className="
        min-h-screen
        flex
        flex-col
        items-center
        justify-center
        gap-5
        px-5
        py-12
        text-center
        sm:gap-6
        sm:px-6
      "
      style={{
        background: "#080808",
      }}
    >
      <div className="text-5xl sm:text-7xl">
        🔍
      </div>

      <h1
        className="
          max-w-full
          font-extrabold
          leading-tight
          text-white
        "
        style={{
          fontSize: "clamp(1.8rem, 7vw, 3.5rem)",
        }}
      >
        Service{" "}
        <span className="text-orange-500">
          Not Found
        </span>
      </h1>

      <p
        className="
          max-w-sm
          text-xs
          leading-5
          sm:text-sm
          sm:leading-6
        "
        style={{
          color: "#666",
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        The service you're looking for doesn't exist or may
        have been moved.
      </p>

      <Link
        to="/services"
        className="
          inline-flex
          min-h-[44px]
          items-center
          justify-center
          rounded-full
          px-6
          py-3
          text-xs
          font-bold
          text-black
          no-underline
          sm:px-8
          sm:py-3.5
          sm:text-sm
        "
        style={{
          background:
            "linear-gradient(135deg,#f97316,#c2410c)",
          boxShadow:
            "0 0 22px rgba(249,115,22,0.45)",
        }}
      >
        ← View All Services
      </Link>
    </div>
  );
}

const TABS = [
  "overview",
  "features",
  "process",
  "pricing",
  "faq",
];

// ─────────────────────────────────────────────────────────────
// CONTACT MODAL SERVICE MAPPING
// ─────────────────────────────────────────────────────────────
// The service details page uses the shared ContactModal, but the
// enquiry form uses its own service/sub-service labels. These maps
// keep both sides consistent without changing the visible page UI.

const contactServiceMap = {
  // Software Development
  "custom-software": "Software Development",
  "mobile-app-development": "Software Development",
  "web-development": "Software Development",
  "ui-ux-design": "Software Development",
  "crm-solutions": "Software Development",
  "mlm-software": "Software Development",

  // API Services
  "business-sms": "API Services",
  "secure-otp": "API Services",

  // Legal Services
  "business-registration": "Legal Services",
  "company-registration": "Legal Services",
  "taxation-services": "Legal Services",
  "iso-certification": "Legal Services",
  "ipr-services": "Legal Services",
};

const contactSubServiceMap = {
  // Software Development
  "custom-software": "Custom Software Development",
  "mobile-app-development": "Mobile App Development",
  "web-development": "Web Development",
  "ui-ux-design": "UI/UX Design",
  "crm-solutions": "CRM Solutions",
  "mlm-software": "MLM Software",

  // API Services
  "business-sms": "WhatsApp API Services",
  "secure-otp": "SMS OTP Services",

  // Legal Services
  "business-registration": "Business Registration",
  "company-registration": "Company Registration",
  "taxation-services": "Taxation Services",
  "iso-certification": "ISO Certification",
  "ipr-services": "IPR Services",
};

// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────

export default function ServiceDetailsPage() {
  const { slug } = useParams();

  const service = ServicesData.find(
    (s) => s.slug === slug
  );

  const [activeTab, setActiveTab] =
    useState("overview");

  const [openFaq, setOpenFaq] = useState(null);

  const [hoveredPlan, setHoveredPlan] =
    useState(null);

  const [openModal, setOpenModal] =
    useState(false);

  if (!service) {
    return <ServiceNotFound />;
  }

  // Values passed into the shared enquiry modal.
  // The fallback keeps the modal usable even if a new service is
  // added before its mapping is explicitly configured.
  const contactService =
    contactServiceMap[service.slug] || "";

  const contactSubService =
    contactSubServiceMap[service.slug] || service.title || "";

  return (
    <div
      className="
        min-h-screen
        w-full
        overflow-x-hidden
        text-white
      "
      style={{
        background: "#080808",
        fontFamily: "'Syne', sans-serif",
      }}
    >
      {/* =====================================================
          GLOBAL STYLES
      ====================================================== */}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

        .body-font {
          font-family: 'DM Sans', sans-serif !important;
        }

        ::-webkit-scrollbar {
          width: 3px;
        }

        ::-webkit-scrollbar-track {
          background: #080808;
        }

        ::-webkit-scrollbar-thumb {
          background: #f97316;
          border-radius: 2px;
        }

        .oglow {
          box-shadow:
            0 0 22px rgba(249,115,22,.45),
            0 0 55px rgba(249,115,22,.15);
        }

        .oglow-sm {
          box-shadow:
            0 0 12px rgba(249,115,22,.35);
        }

        .card {
          background:#0d0d0d;
          border:1px solid rgba(255,255,255,.05);
          border-radius:1rem;
          transition:
            border-color .25s,
            box-shadow .25s,
            transform .25s;
        }

        .card:hover {
          border-color:rgba(249,115,22,.45);
          box-shadow:
            0 0 26px rgba(249,115,22,.18);
          transform:translateY(-4px);
        }

        .pglow {
          box-shadow:
            0 0 8px rgba(249,115,22,.55);
        }

        .orange-grad {
          background:
            linear-gradient(
              135deg,
              #f97316,
              #c2410c
            );
        }

        .orange-text-grad {
          background:
            linear-gradient(
              135deg,
              #f97316,
              #fbbf24
            );
          -webkit-background-clip:text;
          -webkit-text-fill-color:transparent;
          background-clip:text;
        }

        .hero-grid {
          background-image:
            linear-gradient(
              rgba(249,115,22,.05) 1px,
              transparent 1px
            ),
            linear-gradient(
              90deg,
              rgba(249,115,22,.05) 1px,
              transparent 1px
            );

          background-size:56px 56px;
        }

        .tab-active {
          background:
            linear-gradient(
              135deg,
              #f97316,
              #c2410c
            );
          color:#000;
          font-weight:700;
        }

        .tab-inactive {
          background:#111;
          color:#666;
          border:1px solid #1e1e1e;
        }

        .tab-inactive:hover {
          border-color:rgba(249,115,22,.4);
          color:#f97316;
        }

        .dot {
          width:7px;
          height:7px;
          border-radius:50%;
          background:#f97316;
          box-shadow:0 0 10px #f97316;
        }

        @media (max-width: 639px) {
          .card:hover {
            transform:none;
          }

          .hero-grid {
            background-size:38px 38px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            scroll-behavior:auto !important;
          }
        }
      `}</style>

      {/* =====================================================
          HERO
      ====================================================== */}

      <section
        className="
          relative
          flex
          min-h-[500px]
          items-center
          justify-center
          overflow-hidden
          py-8
          sm:min-h-[540px]
          sm:py-10
          md:min-h-[580px]
          md:py-12
          lg:min-h-[600px]
          lg:py-8
        "
      >
        {/* Background */}

        <motion.div
          initial={{
            scale: 1.12,
          }}
          animate={{
            scale: 1,
          }}
          transition={{
            duration: 2.2,
            ease,
          }}
          className="absolute inset-0"
        >
          <img
            src={service.image}
            alt={service.title}
            className="
              h-full
              w-full
              object-cover
            "
            style={{
              filter:
                "brightness(.2) saturate(.3)",
            }}
          />

          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom,rgba(8,8,8,.5),rgba(8,8,8,.85) 60%,#080808)",
            }}
          />
        </motion.div>

        <div className="hero-grid absolute inset-0" />

        {/* Hero Glow */}

        <div
          className="
            absolute
            bottom-0
            left-1/2
            h-[180px]
            w-[280px]
            -translate-x-1/2
            rounded-full
            sm:h-[220px]
            sm:w-[500px]
            md:h-[280px]
            md:w-[700px]
          "
          style={{
            background:
              "radial-gradient(ellipse,rgba(249,115,22,.16),transparent 70%)",
            filter: "blur(32px)",
          }}
        />

        {/* Floating Icons */}

        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="
                absolute
                text-2xl
                sm:text-3xl
                md:text-4xl
              "
              style={{
                opacity: 0.04,
                left: `${(i * 43 + 5) % 93}%`,
                top: `${(i * 31 + 8) % 88}%`,
              }}
              animate={{
                y: [0, -18, 0],
                rotate: [0, 360],
              }}
              transition={{
                duration: 15 + i * 2,
                repeat: Infinity,
                ease: "linear",
              }}
            >
              {service.icon}
            </motion.div>
          ))}
        </div>

        {/* Hero Content */}

        <motion.div
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="
            relative
            z-10
            mx-auto
            w-full
            max-w-3xl
            px-4
            text-center
            sm:px-6
            md:px-8
          "
          style={{
            paddingTop: 45,
          }}
        >
          <motion.p
            variants={fadeUp}
            className="
              my-2
              text-[9px]
              font-bold
              uppercase
              tracking-[0.18em]
              text-orange-500
              sm:my-3
              sm:text-xs
              sm:tracking-widest
            "
          >
            ◈ Premium Service
          </motion.p>

          <motion.h1
            variants={fadeUp}
            className="
              mb-4
              break-words
              font-extrabold
              leading-[1.05]
              tracking-tight
              text-2xl
              sm:mb-5
              sm:text-3xl
              md:text-4xl
              lg:mb-6
              lg:text-5xl
            "
          >
            {service.title.split(" ").map((w, i) => (
              <span
                key={i}
                className={
                  i % 2 === 1
                    ? "text-orange-500"
                    : "text-white"
                }
              >
                {w}{" "}
              </span>
            ))}
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="
              body-font
              mx-auto
              mb-7
              max-w-xl
              px-2
              text-xs
              leading-5
              sm:mb-8
              sm:px-0
              sm:text-sm
              sm:leading-6
              md:mb-10
              md:text-base
              md:leading-relaxed
            "
            style={{
              color: "#888",
            }}
          >
            {service.shortDescription}
          </motion.p>

          {/* Hero Buttons */}

          <motion.div
            variants={fadeUp}
            className="
              flex
              w-full
              flex-col
              items-stretch
              justify-center
              gap-2.5
              px-2
              sm:flex-row
              sm:items-center
              sm:gap-3
              sm:px-0
            "
          >
            <button
              onClick={() => {
                setOpenModal(true);
              }}
              className="
                group
                inline-flex
                min-h-[44px]
                items-center
                justify-center
                rounded-full
                bg-primaryColor
                px-5
                py-3
                text-xs
                font-bold
                text-black
                transition-all
                duration-200
                ease-out
                hover:-translate-y-0.5
                hover:brightness-110
                active:translate-y-[2px]
                active:scale-[0.97]
                active:brightness-95
                sm:px-7
                sm:py-3.5
                sm:text-sm
                md:px-8
              "
            >
              <span className="flex items-center gap-1.5 sm:gap-2">
                Schedule a Consultation

                <span className="
                  transition-transform
                  duration-200
                  group-hover:translate-x-1
                  group-active:translate-x-0
                ">
                  →
                </span>
              </span>
            </button>

            <button
              onClick={() => {
                document
                  .getElementById(
                    "success-stories"
                  )
                  ?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
              }}
              className="
                group
                inline-flex
                min-h-[44px]
                items-center
                justify-center
                rounded-full
                border
                border-primaryColor/40
                bg-transparent
                px-5
                py-3
                text-xs
                font-semibold
                text-primaryColor
                transition-all
                duration-200
                ease-out
                hover:-translate-y-0.5
                hover:border-primaryColor
                hover:bg-primaryColor/10
                active:translate-y-[2px]
                active:scale-[0.97]
                sm:px-7
                sm:py-3.5
                sm:text-sm
                md:px-8
              "
            >
              <span className="flex items-center gap-1.5 sm:gap-2">
                View Portfolio

                <span className="
                  transition-transform
                  duration-200
                  group-hover:translate-x-1
                  group-active:translate-x-0
                ">
                  →
                </span>
              </span>
            </button>
          </motion.div>
        </motion.div>
      </section>

      {/* =====================================================
          STATS
      ====================================================== */}

      <section
        className="
          relative
          bg-[#050505]
          px-3
          py-6
          sm:px-6
          sm:py-8
          md:py-10
        "
      >
        <div className="
          mx-auto
          w-full
          max-w-5xl
        ">
          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.2,
            }}
            transition={{
              duration: 0.5,
            }}
            className="
              grid
              grid-cols-3
              gap-2
              sm:gap-4
            "
          >
            {[
              {
                value: "250+",
                label: "Projects Completed",
              },
              {
                value: "98%",
                label: "Client Satisfaction",
              },
              {
                value: "40%",
                label: "Avg. Time Savings",
              },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{
                  opacity: 0,
                  y: 15,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.08,
                }}
                className="
                  flex
                  min-h-[92px]
                  items-center
                  justify-center
                  rounded-xl
                  border
                  border-white/[0.08]
                  bg-[#0b0b0b]
                  px-1.5
                  py-3
                  text-center
                  transition-all
                  duration-300
                  hover:border-primaryColor/20
                  hover:bg-[#0e0e0e]
                  sm:min-h-[125px]
                  sm:rounded-2xl
                  sm:px-4
                  md:px-5
                "
              >
                <div>
                  <div
                    className="
                      font-extrabold
                      leading-none
                      orange-text-grad
                      text-[1.35rem]
                      sm:text-[2rem]
                      md:text-[2.7rem]
                    "
                  >
                    {stat.value}
                  </div>

                  <p
                    className="
                      body-font
                      mt-1.5
                      text-[7px]
                      font-medium
                      uppercase
                      leading-3
                      tracking-[0.08em]
                      sm:mt-2
                      sm:text-[10px]
                      sm:tracking-[0.15em]
                    "
                    style={{
                      color: "#555",
                    }}
                  >
                    {stat.label}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* =====================================================
          STICKY TAB NAV
      ====================================================== */}

      <div
        className="
          sticky
          top-0
          z-40
          w-full
          overflow-hidden
        "
        style={{
          background:
            "rgba(8,8,8,.93)",
          backdropFilter:
            "blur(20px)",
          borderBottom:
            "1px solid rgba(255,255,255,.04)",
        }}
      >
        <div
          className="
            mx-auto
            flex
            w-full
            max-w-5xl
            gap-1.5
            overflow-x-auto
            px-3
            py-2.5
            scrollbar-none
            sm:justify-center
            sm:gap-2
            sm:px-6
            sm:py-3.5
          "
          style={{
            scrollbarWidth: "none",
          }}
        >
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() =>
                setActiveTab(tab)
              }
              className={`
                flex
                min-h-[36px]
                shrink-0
                items-center
                justify-center
                rounded-full
                px-3
                py-1.5
                text-[10px]
                font-semibold
                capitalize
                cursor-pointer
                border-0
                sm:min-h-0
                sm:px-5
                sm:py-2
                sm:text-sm
                ${
                  activeTab === tab
                    ? "tab-active"
                    : "tab-inactive"
                }
              `}
              style={{
                transition: "all .2s",
              }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* =====================================================
          TAB CONTENT
      ====================================================== */}

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{
            opacity: 0,
            y: 14,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          exit={{
            opacity: 0,
            y: -14,
          }}
          transition={{
            duration: 0.26,
          }}
          className="
            mx-auto
            w-full
            max-w-5xl
            px-3
            py-7
            sm:px-6
            sm:py-10
          "
        >
          {/* =================================================
              OVERVIEW
          ================================================== */}

          {activeTab === "overview" && (
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="visible"
            >
              <motion.p
                variants={fadeUp}
                className="
                  text-center
                  text-[9px]
                  font-bold
                  uppercase
                  tracking-[0.16em]
                  text-orange-500
                  sm:text-xs
                  sm:tracking-widest
                "
              >
                What We Offer
              </motion.p>

              <motion.h2
                variants={fadeUp}
                className="
                  mt-2
                  mb-3
                  text-center
                  text-2xl
                  font-extrabold
                  leading-tight
                  sm:mb-4
                  sm:text-3xl
                  md:text-4xl
                "
              >
                About{" "}
                <span className="text-orange-500">
                  {service.title}
                </span>
              </motion.h2>

              <div
                className="
                  mx-auto
                  mb-7
                  h-px
                  w-16
                  sm:mb-12
                  sm:w-24
                "
                style={{
                  background:
                    "linear-gradient(90deg,transparent,#f97316,transparent)",
                }}
              />

              <motion.p
                variants={fadeUp}
                className="
                  body-font
                  mx-auto
                  mb-8
                  max-w-2xl
                  text-center
                  text-sm
                  leading-6
                  sm:mb-16
                  sm:text-lg
                  sm:leading-relaxed
                "
                style={{
                  color: "#777",
                }}
              >
                {service.fullDescription}
              </motion.p>

              {/* Benefits */}

              <motion.h3
                variants={fadeUp}
                className="
                  mb-4
                  flex
                  items-center
                  gap-2
                  text-base
                  font-bold
                  text-orange-500
                  sm:mb-5
                  sm:text-xl
                "
              >
                ◈ Key Benefits
              </motion.h3>

              <div
                className="
                  mb-10
                  grid
                  grid-cols-1
                  gap-3
                  sm:mb-16
                  sm:grid-cols-2
                  sm:gap-4
                "
              >
                {service.benefits.map((b, i) => (
                  <motion.div
                    key={i}
                    variants={slideL}
                    whileHover={{
                      x: 5,
                    }}
                    className="
                      card
                      flex
                      items-start
                      gap-3
                      p-4
                      sm:gap-4
                      sm:p-6
                    "
                  >
                    <span className="
                      flex-shrink-0
                      text-2xl
                      sm:text-3xl
                    ">
                      {b.icon}
                    </span>

                    <div className="min-w-0">
                      <h4 className="
                        mb-1
                        text-sm
                        font-bold
                        sm:mb-1.5
                        sm:text-base
                      ">
                        {b.title}
                      </h4>

                      <p
                        className="
                          body-font
                          text-xs
                          leading-5
                          sm:text-sm
                          sm:leading-relaxed
                        "
                        style={{
                          color: "#666",
                        }}
                      >
                        {b.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Case Studies */}

              <motion.h3
                id="success-stories"
                variants={fadeUp}
                className="
                  mb-4
                  flex
                  items-center
                  gap-2
                  text-base
                  font-bold
                  text-orange-500
                  sm:mb-5
                  sm:text-xl
                "
              >
                ◈ Success Stories
              </motion.h3>

              <div
                className="
                  grid
                  grid-cols-1
                  gap-3
                  sm:grid-cols-2
                  sm:gap-4
                "
              >
                {service.caseStudies.map(
                  (cs, i) => (
                    <motion.div
                      key={i}
                      variants={scaleUp}
                      whileHover={{
                        scale: 1.02,
                      }}
                      className="
                        group
                        relative
                        h-[190px]
                        cursor-pointer
                        overflow-hidden
                        rounded-2xl
                        sm:h-[220px]
                      "
                      style={{
                        border:
                          "1px solid rgba(255,255,255,.05)",
                      }}
                    >
                      <img
                        src={cs.image}
                        alt={cs.title}
                        className="
                          h-full
                          w-full
                          object-cover
                          transition-transform
                          duration-500
                          group-hover:scale-105
                        "
                        style={{
                          filter:
                            "brightness(.45) saturate(.5)",
                        }}
                      />

                      <div
                        className="
                          absolute
                          inset-0
                          flex
                          flex-col
                          justify-end
                          p-4
                          sm:p-6
                        "
                        style={{
                          background:
                            "linear-gradient(to top,rgba(0,0,0,.92) 40%,transparent)",
                        }}
                      >
                        <h4 className="
                          mb-1
                          text-sm
                          font-bold
                          sm:text-base
                        ">
                          {cs.title}
                        </h4>

                        <p
                          className="
                            body-font
                            mb-1.5
                            text-[10px]
                            sm:mb-2
                            sm:text-xs
                          "
                          style={{
                            color: "#666",
                          }}
                        >
                          {cs.client}
                        </p>

                        <span
                          className="
                            text-xs
                            font-semibold
                            sm:text-sm
                          "
                          style={{
                            color: "#4ade80",
                          }}
                        >
                          {cs.result}
                        </span>
                      </div>

                      <div className="
                        absolute
                        right-3
                        top-3
                        dot
                      " />
                    </motion.div>
                  )
                )}
              </div>
            </motion.div>
          )}

          {/* =================================================
              FEATURES
          ================================================== */}

          {activeTab === "features" && (
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="visible"
            >
              <motion.p
                variants={fadeUp}
                className="
                  text-center
                  text-[9px]
                  font-bold
                  uppercase
                  tracking-[0.16em]
                  text-orange-500
                  sm:text-xs
                  sm:tracking-widest
                "
              >
                Capabilities
              </motion.p>

              <motion.h2
                variants={fadeUp}
                className="
                  mt-2
                  mb-3
                  text-center
                  text-2xl
                  font-extrabold
                  leading-tight
                  sm:mb-4
                  sm:text-3xl
                  md:text-4xl
                  lg:text-[3.5rem]
                "
              >
                Features &{" "}
                <span className="text-orange-500">
                  Capabilities
                </span>
              </motion.h2>

              <div
                className="
                  mx-auto
                  mb-8
                  h-px
                  w-16
                  sm:mb-14
                  sm:w-24
                "
                style={{
                  background:
                    "linear-gradient(90deg,transparent,#f97316,transparent)",
                }}
              />

              {/* Feature Cards */}

              <div
                className="
                  mb-10
                  grid
                  grid-cols-1
                  gap-3
                  sm:grid-cols-2
                  sm:gap-4
                  lg:grid-cols-3
                  lg:gap-5
                  sm:mb-16
                "
              >
                {service.features.map(
                  (f, i) => (
                    <motion.div
                      key={i}
                      variants={scaleUp}
                      whileHover={{
                        y: -6,
                      }}
                      className="
                        card
                        group
                        relative
                        overflow-hidden
                        p-4
                        sm:p-6
                        md:p-7
                      "
                    >
                      <div
                        className="
                          absolute
                          right-0
                          top-0
                          h-20
                          w-20
                          opacity-0
                          transition-opacity
                          group-hover:opacity-100
                          sm:h-24
                          sm:w-24
                        "
                        style={{
                          background:
                            "radial-gradient(circle at top right,rgba(249,115,22,.1),transparent 70%)",
                          borderRadius:
                            "0 1rem 0 0",
                        }}
                      />

                      <motion.div
                        whileHover={{
                          rotate: 360,
                        }}
                        transition={{
                          duration: 0.5,
                        }}
                        className="
                          mb-4
                          flex
                          h-11
                          w-11
                          items-center
                          justify-center
                          rounded-xl
                          text-xl
                          sm:mb-5
                          sm:h-14
                          sm:w-14
                          sm:text-2xl
                        "
                        style={{
                          background:
                            "linear-gradient(135deg,rgba(249,115,22,.14),rgba(249,115,22,.04))",
                          border:
                            "1px solid rgba(249,115,22,.2)",
                        }}
                      >
                        {f.icon}
                      </motion.div>

                      <h3 className="
                        mb-1.5
                        text-sm
                        font-bold
                        sm:mb-2
                        sm:text-base
                      ">
                        {f.title}
                      </h3>

                      <p
                        className="
                          body-font
                          text-xs
                          leading-5
                          sm:text-sm
                          sm:leading-relaxed
                        "
                        style={{
                          color: "#666",
                        }}
                      >
                        {f.description}
                      </p>
                    </motion.div>
                  )
                )}
              </div>

              {/* Technologies */}

              <motion.h3
                variants={fadeUp}
                className="
                  mb-4
                  flex
                  items-center
                  gap-2
                  text-base
                  font-bold
                  text-orange-500
                  sm:mb-5
                  sm:text-xl
                "
              >
                ◈ Technology Stack
              </motion.h3>

              <div
                className="
                  grid
                  grid-cols-1
                  gap-3
                  sm:grid-cols-2
                  sm:gap-4
                "
              >
                {service.technologies.map(
                  (t, i) => (
                    <Reveal
                      key={i}
                      variants={slideL}
                    >
                      <div
                        className="
                          card
                          p-4
                          sm:px-6
                          sm:py-5
                        "
                      >
                        <div className="
                          mb-2
                          flex
                          items-center
                          gap-2
                          sm:mb-3
                          sm:gap-3
                        ">
                          <span className="
                            text-lg
                            sm:text-xl
                          ">
                            {t.icon}
                          </span>

                          <span className="
                            text-xs
                            font-semibold
                            sm:text-sm
                          ">
                            {t.name}
                          </span>

                          <span className="
                            ml-auto
                            text-[10px]
                            font-bold
                            text-orange-500
                            sm:text-xs
                          ">
                            {t.level}%
                          </span>
                        </div>

                        <div
                          className="
                            h-1
                            rounded-full
                          "
                          style={{
                            background:
                              "rgba(255,255,255,.06)",
                          }}
                        >
                          <motion.div
                            className="
                              h-full
                              rounded-full
                              pglow
                            "
                            style={{
                              background:
                                "linear-gradient(90deg,#f97316,#fbbf24)",
                            }}
                            initial={{
                              width: 0,
                            }}
                            whileInView={{
                              width: `${t.level}%`,
                            }}
                            viewport={{
                              once: true,
                            }}
                            transition={{
                              duration: 1.2,
                              delay: i * 0.08,
                              ease: "easeOut",
                            }}
                          />
                        </div>
                      </div>
                    </Reveal>
                  )
                )}
              </div>
            </motion.div>
          )}

          {/* =================================================
              PROCESS
          ================================================== */}

          {activeTab === "process" && (
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="visible"
              className="
                mx-auto
                max-w-2xl
              "
            >
              <motion.p
                variants={fadeUp}
                className="
                  mb-2
                  text-center
                  text-[9px]
                  font-bold
                  uppercase
                  tracking-[0.16em]
                  text-orange-500
                  sm:mb-3
                  sm:text-xs
                  sm:tracking-widest
                "
              >
                Methodology
              </motion.p>

              <motion.h2
                variants={fadeUp}
                className="
                  mb-3
                  text-center
                  text-2xl
                  font-extrabold
                  leading-tight
                  sm:mb-4
                  sm:text-3xl
                  md:text-4xl
                  lg:text-[3.5rem]
                "
              >
                Our{" "}
                <span className="text-orange-500">
                  Process
                </span>
              </motion.h2>

              <div
                className="
                  mx-auto
                  mb-8
                  h-px
                  w-16
                  sm:mb-16
                  sm:w-24
                "
                style={{
                  background:
                    "linear-gradient(90deg,transparent,#f97316,transparent)",
                }}
              />

              <div className="relative">
                {/* Vertical line */}

                <div
                  className="
                    absolute
                    bottom-8
                    left-[21px]
                    top-7
                    w-px
                    sm:left-[27px]
                    sm:top-8
                  "
                  style={{
                    background:
                      "linear-gradient(to bottom,#f97316,rgba(249,115,22,.04))",
                    opacity: 0.3,
                  }}
                />

                {service.process.map(
                  (step, i) => (
                    <motion.div
                      key={i}
                      variants={
                        i % 2 === 0
                          ? slideL
                          : slideR
                      }
                      className="
                        mb-5
                        flex
                        items-start
                        gap-3
                        sm:mb-7
                        sm:gap-5
                      "
                    >
                      <motion.div
                        whileHover={{
                          scale: 1.1,
                        }}
                        className="
                          z-10
                          flex
                          h-[44px]
                          w-[44px]
                          flex-shrink-0
                          items-center
                          justify-center
                          rounded-full
                          text-xs
                          font-extrabold
                          text-black
                          oglow
                          orange-grad
                          sm:h-14
                          sm:w-14
                          sm:text-sm
                        "
                      >
                        {String(
                          step.step
                        ).padStart(2, "0")}
                      </motion.div>

                      <div
                        className="
                          card
                          min-w-0
                          flex-1
                          p-3.5
                          sm:px-6
                          sm:py-5
                        "
                      >
                        <div className="
                          mb-1.5
                          flex
                          flex-wrap
                          items-center
                          gap-1.5
                          sm:mb-2
                          sm:gap-2.5
                        ">
                          <span className="
                            text-lg
                            sm:text-xl
                          ">
                            {step.icon}
                          </span>

                          <h3 className="
                            min-w-0
                            text-sm
                            font-bold
                            sm:text-base
                          ">
                            {step.title}
                          </h3>

                          <span
                            className="
                              ml-0
                              rounded-full
                              px-2
                              py-0.5
                              text-[9px]
                              font-semibold
                              text-orange-500
                              sm:ml-auto
                              sm:px-2.5
                              sm:text-xs
                            "
                            style={{
                              border:
                                "1px solid rgba(249,115,22,.25)",
                              background:
                                "rgba(249,115,22,.08)",
                            }}
                          >
                            {step.duration}
                          </span>
                        </div>

                        <p
                          className="
                            body-font
                            text-xs
                            leading-5
                            sm:text-sm
                            sm:leading-relaxed
                          "
                          style={{
                            color: "#666",
                          }}
                        >
                          {step.description}
                        </p>
                      </div>
                    </motion.div>
                  )
                )}
              </div>
            </motion.div>
          )}

          {/* =================================================
              PRICING
          ================================================== */}

          {activeTab === "pricing" && (
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="visible"
            >
              <motion.p
                variants={fadeUp}
                className="
                  mb-2
                  text-center
                  text-[9px]
                  font-bold
                  uppercase
                  tracking-[0.16em]
                  text-orange-500
                  sm:mb-3
                  sm:text-xs
                  sm:tracking-widest
                "
              >
                Invest in Growth
              </motion.p>

              <motion.h2
                variants={fadeUp}
                className="
                  mb-2
                  text-center
                  text-2xl
                  font-extrabold
                  leading-tight
                  sm:mb-3
                  sm:text-3xl
                  md:text-4xl
                  lg:text-[3.5rem]
                "
              >
                Flexible{" "}
                <span className="text-orange-500">
                  Pricing
                </span>
              </motion.h2>

              <motion.p
                variants={fadeUp}
                className="
                  body-font
                  mb-3
                  text-center
                  text-xs
                  sm:mb-4
                  sm:text-sm
                "
                style={{
                  color: "#555",
                }}
              >
                Choose the plan that fits your ambition
              </motion.p>

              <div
                className="
                  mx-auto
                  mb-8
                  h-px
                  w-16
                  sm:mb-14
                  sm:w-24
                "
                style={{
                  background:
                    "linear-gradient(90deg,transparent,#f97316,transparent)",
                }}
              />

              <div
                className="
                  mx-auto
                  grid
                  max-w-4xl
                  grid-cols-1
                  gap-4
                  sm:gap-5
                  md:grid-cols-3
                "
              >
                {service.pricing.map(
                  (plan, i) => (
                    <motion.div
                      key={i}
                      variants={scaleUp}
                      whileHover={{
                        y: -8,
                      }}
                      onMouseEnter={() =>
                        setHoveredPlan(i)
                      }
                      onMouseLeave={() =>
                        setHoveredPlan(null)
                      }
                      className="
                        relative
                        overflow-hidden
                        rounded-2xl
                      "
                      style={{
                        background:
                          "#0d0d0d",
                        border:
                          plan.recommended
                            ? "1.5px solid rgba(249,115,22,.6)"
                            : "1px solid rgba(255,255,255,.05)",
                        boxShadow:
                          plan.recommended
                            ? "0 0 24px rgba(249,115,22,.3)"
                            : "none",

                        // Preserve desktop scale.
                        transform:
                          plan.recommended
                            ? undefined
                            : undefined,

                        zIndex:
                          plan.recommended
                            ? 10
                            : 1,
                      }}
                    >
                      {/* Recommended */}

                      {plan.recommended && (
                        <div
                          className="
                            absolute
                            right-3
                            top-3
                            rounded-full
                            px-2.5
                            py-1
                            text-[8px]
                            font-extrabold
                            uppercase
                            tracking-wider
                            text-black
                            sm:right-4
                            sm:top-4
                            sm:px-3
                            sm:text-[10px]
                          "
                          style={{
                            background:
                              "linear-gradient(135deg,#f97316,#c2410c)",
                          }}
                        >
                          Best Value
                        </div>
                      )}

                      {/* Plan Header */}

                      <div
                        className="
                          p-5
                          sm:p-7
                        "
                        style={{
                          borderBottom:
                            "1px solid rgba(255,255,255,.05)",
                          background:
                            plan.recommended
                              ? "rgba(249,115,22,.06)"
                              : "#0a0a0a",
                        }}
                      >
                        <h3 className="
                          mb-3
                          text-base
                          font-extrabold
                          sm:mb-4
                          sm:text-lg
                        ">
                          {plan.name}
                        </h3>

                        <div className="
                          flex
                          items-baseline
                          gap-1
                        ">
                          <span
                            className="
                              font-extrabold
                              orange-text-grad
                              text-3xl
                              sm:text-[2.5rem]
                            "
                          >
                            {plan.price}
                          </span>

                          <span
                            className="
                              body-font
                              text-xs
                              sm:text-sm
                            "
                            style={{
                              color: "#444",
                            }}
                          >
                            / {plan.period}
                          </span>
                        </div>
                      </div>

                      {/* Features */}

                      <div className="
                        p-5
                        sm:p-7
                      ">
                        <ul className="
                          mb-6
                          space-y-2.5
                          sm:mb-7
                          sm:space-y-3
                        ">
                          {plan.features.map(
                            (f, j) => (
                              <li
                                key={j}
                                className="
                                  flex
                                  items-start
                                  gap-2.5
                                  sm:gap-3
                                "
                              >
                                <div
                                  className="
                                    mt-0.5
                                    flex
                                    h-4
                                    w-4
                                    flex-shrink-0
                                    items-center
                                    justify-center
                                    rounded-full
                                    transition-all
                                    duration-300
                                  "
                                  style={{
                                    background:
                                      hoveredPlan ===
                                      i
                                        ? "#f97316"
                                        : "rgba(255,255,255,.06)",
                                  }}
                                >
                                  <svg
                                    viewBox="0 0 24 24"
                                    width="9"
                                    height="9"
                                    fill="none"
                                    stroke={
                                      hoveredPlan ===
                                      i
                                        ? "#000"
                                        : "#444"
                                    }
                                    strokeWidth="3.5"
                                  >
                                    <path
                                      d="M5 13l4 4L19 7"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </div>

                                <span
                                  className="
                                    body-font
                                    text-xs
                                    leading-5
                                    sm:text-sm
                                  "
                                  style={{
                                    color: "#777",
                                  }}
                                >
                                  {f}
                                </span>
                              </li>
                            )
                          )}
                        </ul>

                        <button
                          className="
                            min-h-[42px]
                            w-full
                            rounded-full
                            py-2.5
                            text-xs
                            font-bold
                            cursor-pointer
                            transition-all
                            duration-200
                            sm:py-3
                            sm:text-sm
                          "
                          style={
                            plan.recommended
                              ? {
                                  background:
                                    "linear-gradient(135deg,#f97316,#c2410c)",
                                  color: "#000",
                                  border: "0",
                                  boxShadow:
                                    "0 0 18px rgba(249,115,22,.4)",
                                }
                              : {
                                  border:
                                    "1px solid rgba(249,115,22,.4)",
                                  color: "#f97316",
                                  background:
                                    "transparent",
                                }
                          }
                          onMouseEnter={(e) => {
                            if (
                              !plan.recommended
                            ) {
                              e.currentTarget.style.background =
                                "rgba(249,115,22,.1)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (
                              !plan.recommended
                            ) {
                              e.currentTarget.style.background =
                                "transparent";
                            }
                          }}
                        >
                          Get Started
                        </button>
                      </div>
                    </motion.div>
                  )
                )}
              </div>
            </motion.div>
          )}

          {/* =================================================
              FAQ
          ================================================== */}

          {activeTab === "faq" && (
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="visible"
              className="
                mx-auto
                max-w-2xl
              "
            >
              <motion.p
                variants={fadeUp}
                className="
                  mb-2
                  text-center
                  text-[9px]
                  font-bold
                  uppercase
                  tracking-[0.16em]
                  text-orange-500
                  sm:mb-3
                  sm:text-xs
                  sm:tracking-widest
                "
              >
                Got Questions?
              </motion.p>

              <motion.h2
                variants={fadeUp}
                className="
                  mb-3
                  text-center
                  text-2xl
                  font-extrabold
                  leading-tight
                  sm:mb-4
                  sm:text-3xl
                  md:text-4xl
                  lg:text-[3.5rem]
                "
              >
                Frequently{" "}
                <span className="text-orange-500">
                  Asked
                </span>
              </motion.h2>

              <div
                className="
                  mx-auto
                  mb-8
                  h-px
                  w-16
                  sm:mb-14
                  sm:w-24
                "
                style={{
                  background:
                    "linear-gradient(90deg,transparent,#f97316,transparent)",
                }}
              />

              <div className="space-y-2.5 sm:space-y-3">
                {service.faqs.map(
                  (faq, i) => (
                    <motion.div
                      key={i}
                      variants={fadeUp}
                      className="
                        overflow-hidden
                        rounded-2xl
                        transition-all
                        duration-200
                      "
                      style={{
                        background:
                          "#0d0d0d",
                        border:
                          openFaq === i
                            ? "1px solid rgba(249,115,22,.4)"
                            : "1px solid rgba(255,255,255,.05)",
                      }}
                    >
                      <button
                        onClick={() =>
                          setOpenFaq(
                            openFaq === i
                              ? null
                              : i
                          )
                        }
                        className="
                          flex
                          min-h-[54px]
                          w-full
                          items-center
                          justify-between
                          gap-3
                          bg-transparent
                          px-4
                          py-4
                          text-left
                          cursor-pointer
                          border-0
                          sm:px-6
                          sm:py-5
                        "
                        style={{
                          color: "white",
                        }}
                      >
                        <span className="
                          min-w-0
                          pr-2
                          text-xs
                          font-semibold
                          leading-5
                          sm:pr-4
                          sm:text-sm
                          sm:leading-snug
                        ">
                          {faq.question}
                        </span>

                        <motion.span
                          animate={{
                            rotate:
                              openFaq === i
                                ? 45
                                : 0,
                          }}
                          transition={{
                            duration: 0.2,
                          }}
                          className="
                            flex-shrink-0
                            text-xl
                            leading-none
                            text-orange-500
                            sm:text-2xl
                          "
                        >
                          +
                        </motion.span>
                      </button>

                      <AnimatePresence>
                        {openFaq === i && (
                          <motion.div
                            initial={{
                              height: 0,
                              opacity: 0,
                            }}
                            animate={{
                              height: "auto",
                              opacity: 1,
                            }}
                            exit={{
                              height: 0,
                              opacity: 0,
                            }}
                            transition={{
                              duration: 0.25,
                            }}
                            style={{
                              overflow:
                                "hidden",
                            }}
                          >
                            <p
                              className="
                                body-font
                                px-4
                                pb-4
                                pt-3
                                text-xs
                                leading-5
                                sm:px-6
                                sm:pb-5
                                sm:pt-4
                                sm:text-sm
                                sm:leading-relaxed
                              "
                              style={{
                                color: "#666",
                                borderTop:
                                  "1px solid rgba(255,255,255,.04)",
                              }}
                            >
                              {faq.answer}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )
                )}
              </div>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* =====================================================
          CTA
      ====================================================== */}

      <section
        className="
          relative
          overflow-hidden
          border-t
          border-white/[0.04]
          py-9
          sm:py-12
        "
      >
        <div className="hero-grid absolute inset-0" />

        <div
          className="
            absolute
            left-1/2
            top-1/2
            h-[140px]
            w-[280px]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            sm:h-[180px]
            sm:w-[400px]
            md:h-[200px]
            md:w-[500px]
          "
          style={{
            background:
              "radial-gradient(ellipse,rgba(249,115,22,.14),transparent 70%)",
            filter: "blur(40px)",
          }}
        />

        <div
          className="
            relative
            z-10
            mx-auto
            max-w-2xl
            px-4
            text-center
            sm:px-6
          "
        >
          <Reveal>
            <p className="
              mb-3
              text-[9px]
              font-bold
              uppercase
              tracking-[0.16em]
              text-orange-500
              sm:mb-5
              sm:text-xs
              sm:tracking-widest
            ">
              Let's Build Together
            </p>

            <h2
              className="
                font-extrabold
                leading-tight
              "
              style={{
                fontSize:
                  "clamp(2rem,6vw,4rem)",
              }}
            >
              <span className="text-orange-500" />
              <br />
            </h2>

            <AnimatedHeading
              as="h2"
              parts={[
                "Ready to Transform",
                "Your Business?",
              ]}
              className="
                font-saira
                mb-4
                text-2xl
                font-semibold
                leading-tight
                text-white
                sm:mb-6
                sm:text-4xl
                lg:text-6xl
              "
            />

            <p
              className="
                body-font
                mx-auto
                mb-7
                max-w-lg
                text-xs
                leading-5
                sm:mb-10
                sm:text-base
                sm:leading-relaxed
              "
              style={{
                color: "#555",
              }}
            >
              Let's discuss how our{" "}
              <strong className="text-white">
                {service.title}
              </strong>{" "}
              service can help you achieve your
              goals and outpace the competition.
            </p>

            <div className="
              flex
              justify-center
            ">
              <button
                onClick={() => {
                  setOpenModal(true);
                }}
                className="
                  oglow
                  orange-grad
                  inline-flex
                  min-h-[44px]
                  items-center
                  justify-center
                  rounded-full
                  border-0
                  px-6
                  py-3
                  text-xs
                  font-bold
                  text-black
                  cursor-pointer
                  transition-all
                  duration-300
                  hover:-translate-y-0.5
                  sm:px-9
                  sm:py-4
                  sm:text-sm
                "
              >
                Schedule a Consultation
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* =====================================================
          CONTACT MODAL
      ====================================================== */}

      <ContactModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        initialService={contactService}
        initialSubService={contactSubService}
      />
    </div>
  );
}